import { useCallback, useEffect, useRef, useState } from 'react';

import type { ReactNode } from 'react';

import styles from './Tooltip.module.css';

export interface TooltipProps {
  /** The element which triggers the tooltip on hover. */
  element: ReactNode;
  /** The id of the tooltip element. */
  id: string;
  /** The contents of the tooltip. */
  text: string;
  /** The size of the tooltip. */
  size?: 'small' | 'medium' | 'large';
  /** <span style="color: red">**Obsolete**</span>. Used by Lens only, and should be refactored */
  openUnderTrigger?: boolean;
}

/** Represents a component for a custom tooltip. */
export function Tooltip({ element, id, text, size = 'medium', openUnderTrigger = false }: TooltipProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  /* eslint-disable @typescript-eslint/no-magic-numbers */
  const topOffset = (size === 'small' && 20) || (size === 'large' && 35) || 27;
  const leftOffset = (size === 'small' && 25) || (size === 'large' && 70) || 47;
  /* eslint-enable @typescript-eslint/no-magic-numbers */

  const calculatePosition = useCallback(() => {
    if (containerRef.current && tooltipRef.current) {
      const triggerRect = containerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      let top = triggerRect.top + scrollY + topOffset - tooltipRect.height;
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const left = triggerRect.left + scrollX + leftOffset + (triggerRect.width - tooltipRect.width) / 2;

      // Adjust top position to account for page scrolling
      top -= scrollY;

      // Position tooltip below if it would be above the viewport
      if (top < 0) {
        top = triggerRect.bottom + scrollY;
      }

      setPosition({ top, left });
    }
  }, [leftOffset, topOffset]);

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [setIsOpen]
  );

  function show() {
    setIsOpen(true);
  }

  function hide() {
    setIsOpen(false);
  }

  function toggle() {
    if (isOpen) setIsOpen(false);
    else setIsOpen(true);
  }

  useEffect(() => {
    if (!containerRef.current) return;

    const tooltipContainerRef = containerRef.current;

    tooltipContainerRef.addEventListener('keyup', onKeyUp, true);

    return () => {
      tooltipContainerRef.removeEventListener('keyup', onKeyUp, true);
    };
  }, [onKeyUp]);

  useEffect(() => {
    calculatePosition();
    window.addEventListener('resize', calculatePosition);
    return () => {
      window.removeEventListener('resize', calculatePosition);
    };
  }, [calculatePosition]);

  useEffect(() => {
    // Recalculate position when isOpen changes
    if (isOpen && !openUnderTrigger) {
      calculatePosition();
    }
  }, [isOpen, openUnderTrigger, calculatePosition]);

  return (
    <div
      className={styles['tooltip-container']}
      onBlur={hide}
      onFocus={show}
      onMouseEnter={show}
      onMouseLeave={hide}
      onMouseUp={toggle}
      ref={containerRef}
      role="button"
      tabIndex={-1}
    >
      <div aria-describedby={isOpen ? id : undefined}>{element}</div>
      {isOpen && (
        <span
          ref={tooltipRef}
          className={openUnderTrigger ? styles['tooltip_under-trigger'] : styles[`tooltip_${size}`]}
          id={id}
          role="tooltip"
          style={openUnderTrigger ? {} : { top: position.top, left: position.left }}
        >
          {text}
        </span>
      )}
    </div>
  );
}
